<template>
  <BaseWidget
    :class="['payments-overview-widget', widget.type]"
    :loading="loading" 
    :widget-options="widgetMenuOptions" 
    @refresh-data="refreshData" 
    @set-config="setConfig"
    v-on="$listeners"
  >
    <template v-slot:header>
      <div class="title">
        <span class="title-icon">
        </span>
        {{ title }}
      </div>
      <div class="description">{{ subtitle }} <span class="text-lowercase">{{ periodName }}</span></div>
    </template>
    <template v-slot:content>
      
      <div class="payment-list">
        <div class="payment-list-toggle payment-item" v-b-toggle="'prev-payments-in-collapse-' + widget.id">
          <div class="label"><font-awesome-icon icon="fa-solid fa-square-plus" /><font-awesome-icon icon="fa-solid fa-square-minus" /> {{ $gettext('Payments in') }}</div>
          <div class="amount text-brand">{{ $options.filters.formatNumber(invoicesTotalAmount) }}</div>
        </div>
        <b-collapse visible :id="'prev-payments-in-collapse-' + widget.id">
          <div v-for="(item, index) in invoicePaymentsTotal" :key="'in-' + index" class="payment-item text-appinv">
            <div class="label text-brand">{{ getPaymentTypeName(item.payment_type) }}</div>
            <div class="amount text-brand">{{ $options.filters.formatNumber(item.total) }}</div>
          </div>
        </b-collapse>
      </div>

      <div class="payment-list">
        <div class="payment-list-toggle payment-item" v-b-toggle="'prev-costs-in-collapse-' + widget.id">
          <div class="label"><font-awesome-icon icon="fa-solid fa-square-plus" /><font-awesome-icon icon="fa-solid fa-square-minus" /> {{ $gettext('Payments out') }}</div>
          <div class="amount text-brown">{{ $options.filters.formatNumber(costsTotalAmount) }}</div>
        </div>
        <b-collapse visible :id="'prev-costs-in-collapse-' + widget.id">
          <div v-for="(item, index) in costsTotal" :key="'in-' + index" class="payment-item text-appinv">
            <div class="label text-brown">{{ getPaymentTypeName(item.payment_type) }}</div>
            <div class="amount text-brown">{{ $options.filters.formatNumber(item.total) }}</div>
          </div>
        </b-collapse>
      </div>

    </template>
  </BaseWidget>
</template>

<script>
import moment from 'moment';
import BaseWidget from "@/components/dashboard/widgets/BaseWidget"
import DashboardService from "@/store/services/dashboard.service.js";
export default {
  name: 'previous-payments-widget',
  components: { BaseWidget },
  props: {
    widget: {
      type: Object,
      default: () => {}
    },
  },
  data() {
    return {
      loading: true,
      title: '',
      subtitle: this.$gettext('Payments from'),
      invoicePaymentsTotal: [],
      costsTotal: [],
      periodName: '',
      periodNames: {
        0: this.$gettext('Any period'),
        30: this.$gettext('Last month'),
        90: this.$gettext('Last quarter'),
        365: this.$gettext('Last year')
      },
      config: { period: { days: 0 } },
      filters: {
        start_date: '',
        end_date: '',
        group_by: 'payment_type'
      },
    }
  },
  computed: {
    widgetMenuOptions () {
      if (this.loading) return []
      return [
        { name: this.periodNames[0], event: { name: 'set-config', config: { period: { days: false } } } },
        { name: this.periodNames[30], event: { name: 'set-config', config: { period: { days: 30 } } } },
        { name: this.periodNames[90], event: { name: 'set-config', config: { period: { days: 90 } } } },
        { name: this.periodNames[365], event: { name: 'set-config', config: { period: { days: 365 } } } },
      ]
    },
    invoicesTotalAmount () {
      if (this.loading) return ''
      return this.invoicePaymentsTotal.map(item => item.total).reduce((prev, next) => prev + next, 0)
    },
    costsTotalAmount () {
      if (this.loading) return ''
      return this.costsTotal.map(item => item.total).reduce((prev, next) => prev + next, 0)
    },
  },
  mounted () {
    this.setupWidget()
    this.selectPeriod(this.widget.config)
  },
  methods: {
    setConfig (config) {
      if (config.period) {
        this.selectPeriod(config)
      }
      this.$emit('save-config', this.config)
    },
    selectPeriod (config) {
      this.setupPeriod((config.period?.days) ? config.period.days : false)
      this.setPeriodFilters()
      this.refreshData()
    },
    setupPeriod (days) {
      this.config.period.days = days
      this.periodName = (days) ? this.periodNames[days] : this.periodNames[0]
    },
    setPeriodFilters () {
      if (this.config.period.days) {
        const end_date = new Date()  
        const start_date = new Date(new Date().setDate(end_date.getDate() - this.config.period.days))
        this.filters.end_date = moment(end_date).format('YYYY-MM-DD')
        this.filters.start_date = moment(start_date).format('YYYY-MM-DD')
      } else {
        this.filters.end_date = ''
        this.filters.start_date = ''
      }
    },
    refreshData () {
      this.getData()
    },
    getData () {
      this.loading = true
      // PREVIOUS PAYMENTS
      let filters = {
        invoice__sender_company: window.company_id,
        invoice_status: 'p,s'
      }
      DashboardService.invoicePaymentsTotal({ ...this.filters, ...filters }).then((response) => {
        this.invoicePaymentsTotal = response.data
        filters = { owner_company: window.company_id }
        DashboardService.costsTotal({ ...this.filters, ...filters }).then((response) => {
          this.costsTotal = response.data
          this.loading = false
        })
      })
    },
    setupWidget () {
      this.title = this.$gettext('Previous payments')
    },
    getPaymentTypeName (payment_type) {
      let payment_type_text = ''
      switch (payment_type) {
        case 'notpaid':
          payment_type_text = this.$gettext('Unpaid transactions')
          break;
        case 'credit':
          payment_type_text = this.$gettext('Credit card transactions')
          break;
        case 'bank':
          payment_type_text = this.$gettext('Bank transactions')
          break;
        case 'cash':
          payment_type_text = this.$gettext('Cash transactions')
          break;
      }
      return payment_type_text
    }
  }
}
</script>

<style lang="scss" scoped>
.payments-overview-widget {
  .payment-list {
    .collapse {
      padding-left: 10px;
      margin-left: 4px;
      border-left: 2px solid #e5e7eb;
    }
    .payment-item:not(.payment-list-toggle) {
      font-size: 16px !important;
      padding: 5px 0px;
    }
  }
  .payment-item {
    width: 100%;
    display: flex;
    justify-content: space-between;
    .label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .amount {
      white-space: nowrap;
    }
  }
}
</style>
